// Merged exams primitives.tsx Card/CardHeader (winner: bordered flat-depth variant) + libms Card's optional padding prop; rounded-2xl → semantic rounded-box, font-heading dropped (utility not in the mini substrate).
import { Show, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { IconCapsule, type CategoryHue } from '../category-hue/index.js';

/**
 * The product's surface, matching libms components/ui/Card.tsx: a BORDERLESS
 * white panel that separates from the grey page body by contrast alone —
 * which is exactly Spectrum's background-layer mechanism (using-color:
 * layer 2 content on a layer 1 ground). Callers that want an outline pass
 * `class="border border-base-300"`, as libms pages do.
 *
 * RADIUS corrected 2026-09-02 (user's own card-page check against
 * spectrum.adobe.com/page/cards, then verified directly against Adobe's real
 * components/card/index.css): `--spectrum-card-corner-radius` resolves to
 * `--spectrum-corner-radius-100` = 8px — the SAME shared token table, inline
 * alert and toast were already moved onto in the full-substrate audit. The
 * card's own component CSS names it unambiguously (unlike dialog's, which
 * that audit left open for lack of a citable rule); `rounded-box` (16px,
 * corner-radius-800 — the dialog/toast step) was simply the wrong ramp
 * position, not a deliberate house departure.
 *
 * NO shadow, by law: object-styles reserves drop shadows for TRANSIENT,
 * dismissible surfaces (menus, popovers) — "most components rely on a stark
 * color change… to differentiate themselves". The shadow-card token this
 * once carried for dark-theme edge visibility is retired; the darkest ramp
 * (#1d1d1d on #0e0e0e) separates layers more strongly than the old dark one
 * did, so the crutch is no longer needed either.
 */
export function Card(props: {
  children: JSX.Element;
  /** Extra classes on the surface — an outline (`border border-base-300`), a width, a status tint. */
  class?: string;
  /** The inner padding as a utility (`p-4`, `p-5`); none by default, because most cards carry a CardHeader that pads itself. */
  padding?: string;
  /** Automation id on the surface itself — so a test can scope its assertions to the whole card. */
  id?: string;
}) {
  return (
    <div id={props.id} class={`rounded-500 bg-base-100 ${props.padding ?? ''} ${props.class ?? ''}`}>
      {props.children}
    </div>
  );
}

/**
 * A CARD WITH NO BANDS (2026-10-02, the owner, comparing ours with the cards on
 * adobe.com/home: "look how elegant they look — you don't need header footer in
 * cards always"). Their anatomy, measured on that page: 16px of padding all
 * round, a 24px mark beside a 14px/700 title, a 12px description under it, and
 * the actions sitting at the foot with NO divider, NO tinted band and no second
 * ground. One padded box.
 *
 * `Card` + `CardHeader` stays for the dense screens it was built for — a
 * register's frame, a detail section, anywhere a divider earns its keep by
 * separating a title row from a table. This is for the other kind: a tile that
 * is mostly content, where the bands were drawing three boxes around what reads
 * better as one.
 *
 * IT CARRIES A BORDER, unlike `Card`. That is not a style choice but a
 * consequence of the shell's layers changing the same day: `Card` separates
 * "by contrast alone" from a GREY page body, and the content sheet is WHITE
 * now, so a borderless white tile on it has no edge at all. Object-styles
 * forbids the resting shadow Adobe uses here (0 2px 8px at 8%), and names the
 * alternative it wants instead — layer colour plus a 1px border.
 */
export function CardTile(props: {
  /** The tile's name — 14px/700, the heading role at that size. */
  title: string;
  /** A lucide-solid component before the title; with `hue` it becomes the module identity capsule. */
  icon?: Component<{ size?: number; class?: string }>;
  /** The module's hue from `MODULE_HUE` — identity, never status. */
  hue?: CategoryHue | 'neutral';
  /** One or two lines under the title. */
  description?: string;
  /** Anything between the copy and the verbs — a module's live figures, a short list. */
  children?: JSX.Element;
  /** The foot: the tile's verbs, with no band under them. */
  actions?: JSX.Element;
  /** Extra classes on the surface — a width, a span, a status tint. */
  class?: string;
  /** Automation id on the surface. */
  id?: string;
}) {
  const capsule = () => (props.hue && props.icon ? { hue: props.hue, icon: props.icon } : undefined);
  return (
    <div
      id={props.id}
      class={`flex flex-col gap-2 rounded-500 border border-base-300 bg-base-100 p-4 ${props.class ?? ''}`}
    >
      <div class="flex min-w-0 items-center gap-2">
        <Show when={capsule()} fallback={
          <Show when={props.icon}>
            <span class="flex shrink-0 text-base-subtle">
              <Dynamic component={props.icon!} size={24} aria-hidden="true" />
            </span>
          </Show>
        }>
          {(c) => <IconCapsule hue={c().hue} icon={c().icon} size="s" />}
        </Show>
        {/* h2 for the same reason CardHeader uses one: a tile sits under the
            page's h1, so its title is the second level (UX-13). */}
        {/* No weight or colour utility: spectrum.css §2b already sets h1-h6 to
            700 and paints them gray-900, which IS Adobe's card title (14/700,
            rgb(19,19,19) measured). Writing them here would only override the
            substrate with the body ink. */}
        <h2 class="min-w-0 truncate text-sm leading-[1.3]">{props.title}</h2>
      </div>
      {/* BODY INK, not a muted stop — measured on their card, not inferred
          (2026-10-02, the owner: "its crisp colour, not silent colour"). Their
          description is rgb(41,41,41), the gray-800 BODY ink, the same stop
          their title's gray-900 sits one step above; our muted grey was a
          quieter class of text than Adobe uses here at all. Line-height stays
          the ramp's body 1.5 (theirs measures 18 on 12px); `leading-relaxed`
          would be 1.625 and no Spectrum pairing. */}
      <Show when={props.description}>
        <p class="text-xs text-base-content">{props.description}</p>
      </Show>
      <Show when={props.children}>{props.children}</Show>
      {/* `mt-auto` so a row of tiles of unequal text still lines its verbs up. */}
      <Show when={props.actions}>
        <div class="mt-auto flex flex-wrap items-center gap-2 pt-1">{props.actions}</div>
      </Show>
    </div>
  );
}

/**
 * Titled header row for a Card: h3 title, optional subtitle and trailing
 * actions slot. `icon` alone is the section's gray shape-cue inside the title;
 * with `hue` the same icon becomes the module IDENTITY capsule beside the
 * title block (DEV-GUIDE-002 §12.5) — the "CardHeader twin" the staff and
 * student homes used to hand-roll because this slot did not exist.
 */
export function CardHeader(props: {
  title: string;
  /** A lucide-solid component before the title — the section's shape-cue, or the capsule's glyph when `hue` is set. */
  icon?: Component<{ size?: number; class?: string }>;
  /** The module's hue from `MODULE_HUE`: renders `icon` as a 36px IconCapsule beside the title block instead of the gray cue. Identity, never status. */
  hue?: CategoryHue | 'neutral';
  subtitle?: string;
  actions?: JSX.Element;
}) {
  const capsule = () => (props.hue && props.icon ? { hue: props.hue, icon: props.icon } : undefined);
  return (
    // VERTICAL PADDING corrected 2026-09-02 (same check as the radius above):
    // Adobe's real card is deliberately ASYMMETRIC around the title, not the
    // even py-3.5 (14px/14px) this carried — `padding-block-start:
    // spacing-300` (16px, room from the card's top edge) paired with
    // `content-margin-top: spacing-100` (8px, tight to whatever sits under
    // the title). pt-4/pb-2 land exactly on those two steps. The divider
    // moves the same distance closer to the title as Adobe's spacing-100 gap
    // would, which is the visible effect the user flagged.
    // flex-wrap (2026-10-01, measured at 375px on the copies card). This row is
    // a title block and an optional action, and it did not wrap: on a phone the
    // pair could not fit, so the header pushed the shell's content box 34px wide
    // and the whole page scrolled sideways. Wrapping drops the action under the
    // title instead; gap-y-2 keeps the rows apart when it does, and on one line
    // the row behaves exactly as before.
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-base-300 px-5 pt-4 pb-2">
      <div class="flex min-w-0 items-center gap-3">
        <Show when={capsule()}>{(c) => <IconCapsule hue={c().hue} icon={c().icon} size="m" />}</Show>
        <div class="min-w-0">
          {/* text-sm = font-size-100 = --spectrum-heading-size-xxs, the card
              title's real token (was text-xs, an off-ramp arbitrary value).
              leading-[1.3] is Spectrum's HEADING line-height — the type ramp's
              own text-sm defaults to the BODY pairing (1.5) at this size, since
              14px is usually body text; the card title is a genuine heading
              role at that size, so it opts into the heading treatment
              explicitly rather than inheriting the ramp's default guess. */}
          {/* h2, not h3 (UX-13, 2026-09-18): a card sits directly under the
              page's h1 on every landing, detail and settings screen, so its
              title is the page's SECOND level — the h3 this carried made axe's
              heading-order jump h1→h3 on every one of them. spectrum.css §2b
              inks h1–h3 alike, so nothing painted changes. */}
          <h2 class="flex items-center gap-1.5 text-sm leading-[1.3]">
            <Show when={props.icon && !capsule()}>
              <span class="flex shrink-0 text-base-subtle">
                <Dynamic component={props.icon} size={13} aria-hidden="true" />
              </span>
            </Show>
            {props.title}
          </h2>
          <Show when={props.subtitle}>
            <p class="mt-0.5 text-xs text-base-subtle">{props.subtitle}</p>
          </Show>
        </div>
      </div>
      <Show when={props.actions}>
        <div class="flex shrink-0 items-center gap-2">{props.actions}</div>
      </Show>
    </div>
  );
}
